import { type ReactNode, useMemo } from "react";
import { PopulationSummary, StepConversion } from "@models/population.model";

interface PopulationStatsProps {
  summary: PopulationSummary;
  activeAudienceId: number | null;
  onToggleAudience?: (audienceId: number) => void;
}

interface StepGroup {
  key: string;
  step_name: string;
  cells: StepConversion[];
  preconversions: number;   // step-level rollup across cells
  conversions: number;
  reversions: number;
  dimensioned: boolean;     // split across dimensions → show subtotal + per-cell rows
}

// Group per-(step, dimension) rows back under their step so we can show the
// step-level rollup alongside the dimensioned breakdown (MPN-1171). Preserves
// the backend order (step sequence, then name).
function groupStepConversions(rows: StepConversion[]): StepGroup[] {
  const groups: StepGroup[] = [];
  const byKey = new Map<string, StepGroup>();

  for (const row of rows) {
    const stepId = row.journey_step_id ?? null;
    const stepName = row.step_name ?? row.name ?? "Unknown";
    const key = stepId !== null ? `id:${stepId}` : `name:${stepName}`;

    let group = byKey.get(key);
    if (!group) {
      group = { key, step_name: stepName, cells: [], preconversions: 0, conversions: 0, reversions: 0, dimensioned: false };
      byKey.set(key, group);
      groups.push(group);
    }
    group.cells.push(row);
    group.preconversions += row.preconversions;
    group.conversions += row.conversions;
    group.reversions += row.reversions;
    if (row.journey_step_dimension_name !== null) group.dimensioned = true;
  }

  for (const group of groups) {
    if (group.cells.length > 1) group.dimensioned = true;
  }

  return groups;
}

function StepMetrics({ preconversions, conversions, reversions }: { preconversions: number; conversions: number; reversions: number }) {
  return (
    <div className="text-muted" style={{ fontSize: "0.78rem" }}>
      <span className="me-2">Pre-conversions: <span className="fw-semibold text-info-emphasis">{preconversions.toLocaleString()}</span></span>
      <span className="me-2">Conversions: <span className="fw-semibold text-success">{conversions.toLocaleString()}</span></span>
      <span>Reversions: <span className="fw-semibold text-danger">{reversions.toLocaleString()}</span></span>
    </div>
  );
}

function StatBlock({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="col-12 col-md-6">
      <div className="border rounded-3 p-3 h-100 bg-white">
        <div className="text-uppercase text-muted fw-semibold mb-2" style={{ fontSize: "0.72rem", letterSpacing: "0.04em" }}>
          {title}
        </div>
        {children}
      </div>
    </div>
  );
}

export const PopulationStats = ({ summary, activeAudienceId, onToggleAudience }: PopulationStatsProps) => {
  const stepGroups = useMemo(() => groupStepConversions(summary.conversions_by_step), [summary.conversions_by_step]);

  return (
    <div className="row g-3 mb-3">
      <StatBlock title={`Audiences (${summary.audiences.length})`}>
        {summary.audiences.length === 0 ? (
          <div className="text-muted fst-italic" style={{ fontSize: "0.85rem" }}>No audiences yet.</div>
        ) : (
          <div className="d-flex flex-column gap-1">
            {summary.audiences.map((a) => {
              const isActive = activeAudienceId === a.audience_id;
              return (
                <button
                  key={a.audience_id}
                  type="button"
                  aria-pressed={isActive}
                  onClick={onToggleAudience ? () => onToggleAudience(a.audience_id) : undefined}
                  className={`d-flex justify-content-between align-items-center border rounded px-2 py-1 ${isActive ? "border-primary bg-primary-subtle" : "border-light bg-light"}`}
                  style={{ fontSize: "0.85rem", cursor: onToggleAudience ? "pointer" : "default" }}
                >
                  <span className="text-truncate me-2">{a.name}</span>
                  <span className="d-flex align-items-center gap-1">
                    {a.control_count > 0 && (
                      <span className="badge bg-warning-subtle text-warning-emphasis rounded-pill" title="In control group (held out)">
                        {a.control_count.toLocaleString()} control
                      </span>
                    )}
                    <span className="badge bg-secondary rounded-pill">{a.member_count.toLocaleString()}</span>
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </StatBlock>

      <StatBlock title="Conversions">
        <div className="d-flex flex-column gap-2">
          <div className="d-flex flex-wrap gap-3 pb-2 border-bottom" style={{ fontSize: "0.8rem" }}>
            <span>Pre-conversions: <span className="fw-bold text-info-emphasis">{summary.total_preconversions.toLocaleString()}</span></span>
            <span>Conversions: <span className="fw-bold text-success">{summary.total_conversions.toLocaleString()}</span></span>
            <span>Reversions: <span className="fw-bold text-danger">{summary.total_reversions.toLocaleString()}</span></span>
          </div>

          {stepGroups.length === 0 ? (
            <div className="text-muted fst-italic" style={{ fontSize: "0.85rem" }}>No per-step activity yet.</div>
          ) : (
            stepGroups.map((group) => (
              <div key={group.key}>
                <div className="fw-semibold text-truncate" style={{ fontSize: "0.85rem" }}>{group.step_name}</div>
                {/* Step-level rollup: a plain row when undimensioned, a subtotal when split by dimension. */}
                <StepMetrics preconversions={group.preconversions} conversions={group.conversions} reversions={group.reversions} />
                {group.dimensioned && (
                  <div className="ps-3 mt-1 d-flex flex-column gap-1">
                    {group.cells.map((cell, idx) => (
                      <div key={cell.journey_step_dimension_id ?? `cell-${idx}`}>
                        <div className="text-truncate" style={{ fontSize: "0.82rem" }}>{cell.journey_step_dimension_name ?? "Unspecified"}</div>
                        <StepMetrics preconversions={cell.preconversions} conversions={cell.conversions} reversions={cell.reversions} />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </StatBlock>
    </div>
  );
};
